import { CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CurrencyDollarIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import Tag from 'components/common/tag';
import Button from 'components/inputs/button';
import Tooltip from 'components/layout/tooltip';
import { SystemOptionType } from 'data/types';
import { KeyboardEvent, memo } from 'react';
import { getAirHandlerTypeDisplayLabel } from 'utils/air-handler-input-helpers';
import { formatPrice } from 'utils/helpers';
import { isPtacSelectedSystem } from 'utils/ptac-selected-system';
import { getSystemProductImages } from 'utils/system-products';
import { coverageSidesForSystem } from './coverage-sides';
import {
  pickDescriptionFor,
  PickKind,
  pickLabelFor
} from './equipment-comparison-utils';
import { CoverageGrid } from './overview-cards';
import ProductImageCarousel from './product-image-carousel';
import ProductImageStrip from './product-image-strip';
import ScoreRing from './score-ring';
import SystemCategoryScoreBars from './system-category-score-bars';
import {
  getSystemConfigurationSummaryLines,
  systemOptionIncludesDuctedAirHandler
} from './system-configuration-summary';
import { buildSystemTitleTags, FILTER_MISMATCH_TAG_KEY } from './system-tags';
import { TitleTagChip } from './system-title-tags';
import { SystemListViewMode } from './view-mode';

type TopPickCardProps = Readonly<{
  system: SystemOptionType;
  kind: PickKind | null;
  isSelected: boolean;
  onSelect: (system: SystemOptionType) => void;
  onUnselect?: (system: SystemOptionType) => void;
  onAddToCompare?: (system: SystemOptionType, kind: PickKind | null) => void;
  showTopMatchBadge?: boolean;
  compact?: boolean;
  isAddedToCompare?: boolean;
  hasRebates?: boolean;
  systemNumber?: number;
  viewMode?: SystemListViewMode;
  showCoverage?: boolean;
}>;

type CompareToggleButtonProps = Readonly<{
  added: boolean;
  onToggle: () => void;
}>;

function CompareToggleButton({ added, onToggle }: CompareToggleButtonProps) {
  return (
    <Button
      color={added ? 'green' : 'secondary'}
      text={added ? 'Added' : 'Compare'}
      icon={added ? CheckIcon : PlusIcon}
      iconSize='sm'
      onClick={(event) => {
        event?.stopPropagation();
        onToggle();
      }}
    />
  );
}

function TopPickCard({
  system,
  kind,
  isSelected,
  onSelect,
  onUnselect,
  onAddToCompare,
  showTopMatchBadge,
  compact,
  isAddedToCompare,
  hasRebates,
  systemNumber,
  viewMode = 'compact',
  showCoverage = false
}: TopPickCardProps) {
  const isDetailed = viewMode === 'detailed';
  const isPtac = isPtacSelectedSystem(system);
  const productImages = isDetailed ? getSystemProductImages(system) : [];
  const showImageCarousel = !isPtac && productImages.length > 0;
  const showUnitImageStrip = isPtac && productImages.length > 0;
  const { condenserLine, zoneLine } =
    getSystemConfigurationSummaryLines(system);
  const totalScore = system.score?.total ?? 0;
  let label: string;
  if (kind) {
    label = pickLabelFor(kind);
  } else if (system.tags.includes('Custom') && system.name) {
    label = system.name;
  } else if (systemNumber !== undefined) {
    label = `System ${systemNumber}`;
  } else {
    label = system.name || 'System';
  }
  const description = kind ? pickDescriptionFor(kind) : null;

  const titleTagItems = buildSystemTitleTags(
    system.tags,
    showTopMatchBadge === true
  );
  const mismatchTagItem = titleTagItems.find(
    (item) => item.key === FILTER_MISMATCH_TAG_KEY
  );
  const displayTagItems = titleTagItems.filter(
    (item) => item.key !== FILTER_MISMATCH_TAG_KEY
  );
  const [firstTagItem] = displayTagItems;
  const remainingTagItems = displayTagItems.slice(1);
  const includesDuctedAirHandler = systemOptionIncludesDuctedAirHandler(system);
  const hasChipRow =
    firstTagItem !== undefined ||
    mismatchTagItem !== undefined ||
    hasRebates === true ||
    includesDuctedAirHandler;

  const handleSelect = () => onSelect(system);

  const handleToggleCompare = () => {
    if (isAddedToCompare) {
      onUnselect?.(system);
    } else {
      onAddToCompare?.(system, kind);
    }
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect();
    }
  };

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleCardKeyDown}
      className={clsx(
        'group relative flex h-full w-full cursor-pointer flex-col rounded-lg border p-4 text-left shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        isSelected
          ? 'border-blue-500 bg-blue-50 ring-[3px] ring-blue-100'
          : 'border-gray-200 bg-white hover:border-blue-300'
      )}
    >
      <div className='grow'>
        {showImageCarousel && (
          <div className='mb-2'>
            <ProductImageCarousel products={productImages} thumbSize='sm' />
          </div>
        )}
        {hasChipRow && (
          <div className='mb-2 flex flex-wrap items-center gap-1'>
            {firstTagItem && (
              <span title={firstTagItem.tooltip}>
                <TitleTagChip item={firstTagItem} />
              </span>
            )}
            {remainingTagItems.length > 0 && (
              <div className='relative'>
                <Tag color='default' text={`+${remainingTagItems.length}`} />
                <Tooltip>
                  <div className='flex flex-col gap-0.5'>
                    {remainingTagItems.map((item) => (
                      <span key={item.key}>{item.label}</span>
                    ))}
                  </div>
                </Tooltip>
              </div>
            )}
            {mismatchTagItem && (
              <span title={mismatchTagItem.tooltip}>
                <TitleTagChip item={mismatchTagItem} />
              </span>
            )}
            {includesDuctedAirHandler && (
              <Tag
                color='default'
                text={getAirHandlerTypeDisplayLabel('ducted')}
              />
            )}
            {hasRebates && (
              <Tag
                color='green'
                text='Rebates Available'
                icon={CurrencyDollarIcon}
              />
            )}
          </div>
        )}
        <div className='flex items-baseline justify-between gap-2'>
          <div className='font-semibold text-gray-900'>{label}</div>
          <div className='shrink-0 font-bold text-gray-900'>
            {formatPrice(system.price)}
          </div>
        </div>
        {system.configuration.name?.trim() && (
          <div className='mt-0.5 text-xs text-gray-500'>
            Configuration: {system.configuration.name}
          </div>
        )}
        {(!compact || isDetailed) && description && (
          <div className='mt-2 text-xs text-gray-600'>{description}</div>
        )}
        <div className='mt-2 flex flex-col text-xs text-gray-500'>
          <div>{condenserLine}</div>
          <div>{zoneLine}</div>
        </div>
        {showUnitImageStrip && (
          <ProductImageStrip products={productImages} className='mt-3' />
        )}
        {isDetailed && system.score && (
          <div className='mt-3'>
            <SystemCategoryScoreBars score={system.score} />
          </div>
        )}
        {showCoverage && (
          <div className='mt-3'>
            <CoverageGrid sides={coverageSidesForSystem(system)} bordered />
          </div>
        )}
      </div>
      <div
        className={clsx(
          'mt-3 flex items-center justify-between gap-2 border-t pt-2',
          isSelected ? 'border-blue-100' : 'border-gray-100'
        )}
      >
        <div className='flex items-center gap-2'>
          {system.score && (
            <>
              <ScoreRing score={totalScore} />
              <span className='text-xs text-gray-500'>match score</span>
            </>
          )}
        </div>
        {(onAddToCompare || (isAddedToCompare && onUnselect)) && (
          <CompareToggleButton
            added={isAddedToCompare === true}
            onToggle={handleToggleCompare}
          />
        )}
      </div>
    </div>
  );
}

export default memo(TopPickCard);
